import { useCallback, useState } from 'react'

import type { Space } from '@tribeplatform/gql-client/types'
import { useOrganizeSpacesInCollection } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { SortableList } from '@tribeplatform/react-ui-kit/SortableList'
import { Text } from '@tribeplatform/react-ui-kit/Text'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { SpaceImage } from '../Space/SpaceImage.js'

export const CollectionOrganizationModal = ({
  id,
  spaces,
  open,
  onClose,
}: {
  id: string
  spaces: Space[]
  open: boolean
  onClose: () => void
}) => {
  const { $t } = useI18n()
  const { mutateAsync: organizeSpaces, isLoading } =
    useOrganizeSpacesInCollection()
  const [sortedSpaces, setSortedSpaces] = useState(spaces || [])

  const hasSpaces = !!sortedSpaces?.length

  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return
      }

      const newSortedSpaces = SortableList.reorder(
        sortedSpaces,
        result.source.index,
        result.destination.index,
      )

      setSortedSpaces(newSortedSpaces)
    },
    [sortedSpaces],
  )

  const onSave = useCallback(async () => {
    const spaceIds = sortedSpaces.map(({ id }) => id)
    const collectionId = id

    try {
      await organizeSpaces({ collectionId, spaceIds })
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: error.message,
        status: 'error',
      })
      return
    }

    onClose()
  }, [id, onClose, organizeSpaces, sortedSpaces])

  if (!id) {
    return null
  }

  return (
    <Modal open={open} onClose={onClose} scrollable>
      <Modal.Header
        title={$t({
          id: 'Collection.CollectionOrganizationModal.HeaderTitle',
          defaultMessage: 'Organize {SPACE} in the {COLLECTION}',
        })}
        description={$t({
          id: 'Collection.CollectionOrganizationModal.HeaderDescription',
          defaultMessage: 'Customize order by dragging and dropping {SPACES}',
        })}
      />
      <Modal.Content>
        {hasSpaces ? (
          <div className="flex flex-col space-y-2">
            <SortableList
              id="collection-space-items"
              onDragEnd={onDragEnd}
              items={sortedSpaces}
              renderItem={space => (
                <SortableList.Item id={space.id}>
                  <SortableList.DefaultView>
                    <SpaceImage space={space} size="lg" />
                    <div className="truncate">{space.name}</div>
                  </SortableList.DefaultView>
                </SortableList.Item>
              )}
            />
          </div>
        ) : (
          <Text>
            <T
              id="Collection.CollectionOrganizationModal.NoSpaceMessage"
              defaultMessage="No {SPACES} in the {COLLECTION}"
            />
          </Text>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button loading={isLoading} onClick={onSave}>
          <T id="Generics.Save" defaultMessage="Save" />
        </Button>
        <Button variant="secondaryNeutral" onClick={onClose}>
          <T id="Generics.Cancel" defaultMessage="Cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
